import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { currentHomeIdState, homeState } from '@/state/home.state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { HomeDao } from '@rock/db-admin';
import type { HomeId } from '@rock/types';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const log = logger('useToggleHomeDeleteAction');
const homeDao = new HomeDao();

export const useToggleHomeDeleteAction = (homeId: HomeId) => {
    const [confirmHomeToggle, setConfirmHomeToggle] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [currentHomeId, setCurrentHomeId] = useAtom(currentHomeIdState);
    const home = useAsyncAtomValue(homeState(homeId));
    const isRestore = home?.active === false;

    async function doToggleDelete() {
        if (!home) return;
        setLoading(true);

        try {
            if (isRestore) {
                log.info(`Restoring Home: ${homeId}`);
                await homeDao.updateHome(homeId, { active: true });

                toast.success('Home Restored', {
                    description: `Successfully restored home: ${home?.name}`,
                });

                log.info(`Successfully restored home: ${homeId}`);
            } else {
                log.info(`Deleting Home: ${homeId}`);
                await homeDao.updateHome(homeId, { active: false });

                // If the deleted home was the current one, navigate back to home list
                if (homeId === currentHomeId) {
                    setCurrentHomeId(undefined);
                    navigate('/directory/homes');
                }

                toast.success('Home Deleted', {
                    description: `Successfully deleted home: ${home?.name}`,
                });

                log.info(`Successfully deleted home: ${homeId}`);
            }
            setConfirmHomeToggle(false);
        } catch (err) {
            const action = isRestore ? 'Restoring' : 'Deleting';
            log.error(`Error ${action} Home: ${homeId}`, { err });
            toast.error(`Error ${action} Home`, {
                description: 'An unexpected error occurred.',
            });
        } finally {
            setLoading(false);
        }
    }

    const ToggleHomeDeleteDialog = () => (
        <AlertDialog open={confirmHomeToggle} onOpenChange={(open) => setConfirmHomeToggle(open)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{isRestore ? 'Confirm Restore' : 'Confirm Delete'}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {isRestore
                            ? `Are you sure you want to restore ${home?.name}?`
                            : `Are you sure you want to delete ${home?.name}? This can be undone by restoring the home later.`}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={doToggleDelete}
                        disabled={loading}
                        variant={isRestore ? 'default' : 'destructive'}
                    >
                        {loading ? (isRestore ? 'Restoring...' : 'Deleting...') : isRestore ? 'Restore' : 'Delete'}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );

    return {
        setConfirmHomeToggle,
        ToggleHomeDeleteDialog,
    };
};
